/**
 * iOS Safari Compatibility Utilities
 * Handles iOS-specific issues and provides fallbacks
 */

/**
 * Detect if running on iOS
 */
export const isIOS = (): boolean => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

/**
 * Detect if running in iOS Safari (not Chrome or other browsers on iOS)
 */
export const isIOSSafari = (): boolean => {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const notChrome = !/CriOS/.test(ua);
  const notFirefox = !/FxiOS/.test(ua);

  return iOS && webkit && notChrome && notFirefox;
};

/**
 * Safe localStorage wrapper that handles iOS Private Mode
 * In iOS Private Mode, localStorage.setItem throws QuotaExceededError
 */
class SafeStorage {
  private fallbackStorage: Map<string, string> = new Map();
  private isLocalStorageAvailable: boolean = true;

  constructor() {
    // Test if localStorage is available
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
    } catch (e) {
      console.warn('⚠️ localStorage not available (iOS Private Mode?), using fallback memory storage');
      this.isLocalStorageAvailable = false;
    }
  }

  getItem(key: string): string | null {
    if (this.isLocalStorageAvailable) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.error('localStorage.getItem failed:', e);
      }
    }
    return this.fallbackStorage.get(key) || null;
  }

  setItem(key: string, value: string): void {
    if (this.isLocalStorageAvailable) {
      try {
        localStorage.setItem(key, value);
        return;
      } catch (e) {
        console.error('localStorage.setItem failed, switching to fallback:', e);
        this.isLocalStorageAvailable = false;
      }
    }
    this.fallbackStorage.set(key, value);
  }

  removeItem(key: string): void {
    if (this.isLocalStorageAvailable) {
      try {
        localStorage.removeItem(key);
        return;
      } catch (e) {
        console.error('localStorage.removeItem failed:', e);
      }
    }
    this.fallbackStorage.delete(key);
  }

  clear(): void {
    if (this.isLocalStorageAvailable) {
      try {
        localStorage.clear();
        return;
      } catch (e) {
        console.error('localStorage.clear failed:', e);
      }
    }
    this.fallbackStorage.clear();
  }
}

export const safeStorage = new SafeStorage();

/**
 * Fix iOS viewport height issues
 * Call this on resize and orientation change
 */
export const fixIOSViewportHeight = (): void => {
  if (!isIOS()) return;

  // Set CSS custom property with actual viewport height
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

/**
 * Handle iOS keyboard visibility
 * Adds/removes class to body when keyboard is shown/hidden
 */
export const setupIOSKeyboardHandlers = (): void => {
  if (!isIOS()) return;

  const inputs = ['input', 'textarea', 'select'];

  inputs.forEach(selector => {
    // Keyboard shown
    document.addEventListener('focusin', (e) => {
      const target = e.target as HTMLElement;
      if (target && inputs.some(s => target.tagName.toLowerCase() === s)) {
        document.body.classList.add('keyboard-open');
      }
    });

    // Keyboard hidden
    document.addEventListener('focusout', (e) => {
      const target = e.target as HTMLElement;
      if (target && inputs.some(s => target.tagName.toLowerCase() === s)) {
        // Delay to allow for focus switching between inputs
        setTimeout(() => {
          if (document.activeElement?.tagName.toLowerCase() !== 'input' &&
              document.activeElement?.tagName.toLowerCase() !== 'textarea' &&
              document.activeElement?.tagName.toLowerCase() !== 'select') {
            document.body.classList.remove('keyboard-open');
          }
        }, 100);
      }
    });
  });
};

/**
 * Prevent iOS Safari from bouncing (rubber-band effect)
 * Only prevents overscroll bounce, not legitimate scrolling
 */
export const preventIOSBounce = (): void => {
  if (!isIOS()) return;

  // Use CSS overscroll-behavior instead of JavaScript prevention
  // This is more reliable and doesn't block legitimate scrolling
  document.body.style.overscrollBehavior = 'none';
  document.documentElement.style.overscrollBehavior = 'none';
};

/**
 * Fix iOS date input display
 * iOS Safari displays date inputs differently - this provides a consistent experience
 */
export const fixIOSDateInputs = (): void => {
  if (!isIOS()) return;

  const dateInputs = document.querySelectorAll<HTMLInputElement>(
    'input[type="date"], input[type="datetime-local"], input[type="time"]'
  );

  dateInputs.forEach(input => {
    // Add a placeholder if not present
    if (!input.placeholder) {
      if (input.type === 'date') {
        input.placeholder = 'MM/DD/YYYY';
      } else if (input.type === 'datetime-local') {
        input.placeholder = 'MM/DD/YYYY HH:MM';
      } else if (input.type === 'time') {
        input.placeholder = 'HH:MM';
      }
    }

    // Ensure minimum touch target size
    input.style.minHeight = '44px';
  });
};

/**
 * Initialize all iOS compatibility fixes
 * Call this once when the app loads
 */
export const initIOSCompat = (): void => {
  if (!isIOS()) {
    console.log('✅ Not running on iOS, skipping iOS compatibility fixes');
    return;
  }

  console.log('📱 iOS detected, applying compatibility fixes...');

  // Fix viewport height
  fixIOSViewportHeight();
  window.addEventListener('resize', fixIOSViewportHeight);
  window.addEventListener('orientationchange', fixIOSViewportHeight);

  // Setup keyboard handlers
  setupIOSKeyboardHandlers();

  // Prevent bounce effect
  preventIOSBounce();

  // Fix date inputs after DOM is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixIOSDateInputs);
  } else {
    fixIOSDateInputs();
  }

  // Re-apply date input fixes when navigating (for SPAs)
  const observer = new MutationObserver(() => {
    fixIOSDateInputs();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  console.log('✅ iOS compatibility fixes applied');
};

/**
 * Check if device has camera support
 * iOS requires specific permissions
 */
export const checkCameraSupport = async (): Promise<boolean> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasCamera = devices.some(device => device.kind === 'videoinput');

    if (!hasCamera) {
      console.warn('⚠️ No camera found on this device');
      return false;
    }

    // Test camera permissions
    if (isIOS()) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch (e) {
        console.error('❌ Camera permission denied or unavailable:', e);
        return false;
      }
    }

    return true;
  } catch (e) {
    console.error('❌ Error checking camera support:', e);
    return false;
  }
};
